/**
 * BasketsPage — R3 + R4
 *
 * Lists all deployed strategy portfolios (baskets).
 * Per-basket:
 *   - R3: Aggregate P&L performance tile + per-member breakdown
 *   - R4: Net Signals view — preview netted orders for today
 */
import React, { useState, useEffect, useCallback } from 'react';
import { tradingService } from '../../services/tradingService';
import RebalanceDriftPanel from './portfolio/RebalanceDriftPanel';

const fmt$ = v => v != null
  ? '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })
  : '—';
const fmtPct = (v, decimals = 1) => v != null ? `${v >= 0 ? '+' : ''}${Number(v).toFixed(decimals)}%` : '—';
const fmtPnl = v => v != null ? (v >= 0 ? `+$${Math.abs(v).toLocaleString()}` : `-$${Math.abs(v).toLocaleString()}`) : '—';
const pnlCls = v => v == null ? 'text-gray-400' : v >= 0 ? 'text-green-400' : 'text-red-400';

// ─── Member Row ───────────────────────────────────────────────────────────────
const MemberRow = ({ m }) => (
  <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/30 text-sm">
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.is_active ? 'bg-green-400' : 'bg-gray-400'}`} />
        <span className="text-gray-800 dark:text-gray-200 font-medium truncate">{m.strategy_name}</span>
      </div>
      <div className="flex gap-2 ml-3.5 mt-0.5 text-[10px] text-gray-400">
        <span>{(m.symbols || []).join(', ') || '—'}</span>
        {m.today_signal && (
          <span className={`font-medium ${m.today_signal.type === 'BUY' ? 'text-green-400' : m.today_signal.type === 'SELL' ? 'text-red-400' : 'text-gray-400'}`}>
            {m.today_signal.type} [{m.today_signal.status}]
          </span>
        )}
      </div>
    </div>
    <div className="text-right shrink-0 ml-3">
      <div className="text-brand-gold font-semibold text-xs tabular-nums">{(m.weight * 100).toFixed(0)}%</div>
      <div className={`text-[10px] tabular-nums ${pnlCls(m.unrealized_pnl)}`}>
        {m.unrealized_pnl !== 0 ? fmtPnl(m.unrealized_pnl) : fmt$(m.allocation_usd)}
      </div>
    </div>
  </div>
);

// ─── Net Signal Item ─────────────────────────────────────────────────────────
const NetSignalRow = ({ item }) => (
  <div className="flex items-center justify-between py-2 text-sm">
    <div>
      <span className="font-mono font-medium text-gray-800 dark:text-gray-200 mr-2">{item.symbol}</span>
      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
        item.net_action === 'BUY'  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
        item.net_action === 'SELL' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
        'bg-gray-100 dark:bg-gray-800 text-gray-500'
      }`}>
        {item.net_action === 'FLAT' ? 'CANCELLED' : `${item.net_action} ${item.net_qty}`}
      </span>
      {item.saving_trades && item.net_action !== 'FLAT' && (
        <span className="ml-2 text-[10px] text-brand-gold">↓ reduced</span>
      )}
    </div>
    <div className="text-[10px] text-gray-400 text-right">
      {item.contributors.map(c => (
        <div key={c.strategy_id}>{c.signal_type} {c.qty} — {c.strategy_name.split('·').pop()?.trim()}</div>
      ))}
    </div>
  </div>
);

// ─── Single Basket Card ───────────────────────────────────────────────────────
const BasketCard = ({ portfolio, onDelete, onToggleActive }) => {
  const [expanded,     setExpanded]     = useState(false);
  const [perf,         setPerf]         = useState(null);
  const [perfLoading,  setPerfLoading]  = useState(false);
  const [netSigs,      setNetSigs]      = useState(null);
  const [netLoading,   setNetLoading]   = useState(false);
  const [tab,          setTab]          = useState('members');  // 'members' | 'performance' | 'netsignals'
  const [deleting,     setDeleting]     = useState(false);
  const [toggling,     setToggling]     = useState(false);

  const loadPerf = useCallback(async () => {
    if (perf) return;
    setPerfLoading(true);
    try {
      const res = await tradingService.getPortfolioPerformance(portfolio.id);
      setPerf(res.data);
    } catch { /* ignore */ } finally { setPerfLoading(false); }
  }, [portfolio.id, perf]);

  const loadNetSigs = useCallback(async () => {
    if (netSigs) return;
    setNetLoading(true);
    try {
      const res = await tradingService.getNetSignals(portfolio.id);
      setNetSigs(res.data);
    } catch { /* ignore */ } finally { setNetLoading(false); }
  }, [portfolio.id, netSigs]);

  const handleTabChange = (t) => {
    setTab(t);
    if (t === 'performance') loadPerf();
    if (t === 'netsignals')  loadNetSigs();
  };

  const handleDelete = async () => {
    if (!window.confirm(
      `Delete basket "${portfolio.name}"?\n\n` +
      `Member strategies used ONLY in this basket AND inactive (not trading, no open ` +
      `position) will be permanently deleted with their history. Strategies shared with ` +
      `another basket, still active, or holding a position are KEPT. Optimizations are ` +
      `never deleted.`
    )) return;
    setDeleting(true);
    try { await onDelete(portfolio.id); } finally { setDeleting(false); }
  };

  // Add / remove capital on the basket as an AMOUNT (wishlist #27): + is split by the basket's own rule (no trade now);
  // − comes only from the members' spare cash (cut individual strategies to sell). Previewed, confirmed, audited.
  const handleCapital = async () => {
    const accts = [...new Map((portfolio.members || []).filter(m => m.account)
      .map(m => [m.account, m.account_name || `account ${m.account}`])).entries()];
    if (!accts.length) { alert('This basket has no strategy on an account.'); return; }
    let account = accts[0][0];
    if (accts.length > 1) {
      const pick = window.prompt(`Which account? ${accts.map(([id, n]) => `${id} = ${n}`).join(', ')}`, String(account));
      if (pick == null) return;
      account = Number(pick);
    }
    const raw = window.prompt(`Amount to add (+) or remove (−) for "${portfolio.name}"`, '');
    if (raw == null) return;
    const delta = parseFloat(String(raw).replace(/[$,\s]/g, ''));
    if (isNaN(delta) || delta === 0) return;
    try {
      const pv = (await tradingService.changeBasketCapital(portfolio.id, { account, delta, preview: true })).data;
      const n = Object.keys(pv.credits || {}).length;
      if (!window.confirm(`${delta > 0 ? 'Add' : 'Remove'} ${fmt$(Math.abs(delta))} ${delta > 0 ? 'to' : 'from'} ${n} strategies (${pv.note}).\nNo trade now.\n\nApply?`)) return;
      const r = (await tradingService.changeBasketCapital(portfolio.id, { account, delta })).data;
      alert(`Done — ${r.flows} strategies credited, dated ${r.session}.`);
    } catch (e) {
      alert(`Could not change the basket capital: ${e.response?.data?.error || e.message}`);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    try { await onToggleActive(portfolio.id); } finally { setToggling(false); }
  };

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={() => setExpanded(v => !v)}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-base">{expanded ? '▾' : '▸'}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">{portfolio.name}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                portfolio.is_active
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
              }`}>
                {portfolio.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="flex gap-3 text-[10px] text-gray-400 mt-0.5">
              <span>{portfolio.member_count} strategies</span>
              <span>{portfolio.active_count} active</span>
              <span>{fmt$(portfolio.total_capital)} total</span>
              <span className="capitalize">{portfolio.optimizer_method?.replace('_', ' ')}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3" onClick={e => e.stopPropagation()}>
          {portfolio.is_active && (
            <button onClick={handleCapital}
              className="text-xs px-2.5 py-1 rounded-lg font-medium bg-brand-gold/10 text-brand-gold hover:bg-brand-gold/20 transition-colors">
              Capital
            </button>
          )}
          <button
            onClick={handleToggle}
            disabled={toggling}
            className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
              portfolio.is_active
                ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40'
                : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40'
            }`}
          >
            {toggling ? '…' : portfolio.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="text-xs px-2 py-1 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            {deleting ? '…' : 'Delete'}
          </button>
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-800">
          {/* Sub-tabs */}
          <div className="flex gap-1 px-4 pt-3 border-b border-gray-100 dark:border-gray-800">
            {[
              { key: 'members',     label: 'Members' },
              { key: 'performance', label: 'Performance' },
              { key: 'netsignals',  label: 'Net Signals' },
              // Drift monitoring only applies to sector-cascade baskets (they carry a
              // rebalance recipe + get the nightly ideal-vs-actual check).
              ...(portfolio.optimizer_method === 'sector_cascade'
                ? [{ key: 'drift', label: 'Drift' }] : []),
            ].map(t => (
              <button
                key={t.key}
                onClick={() => handleTabChange(t.key)}
                className={`px-3 py-1.5 text-xs rounded-t-lg transition-colors ${
                  tab === t.key
                    ? 'bg-brand-gold/10 text-brand-gold border-b-2 border-brand-gold'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-4">
            {/* Members tab */}
            {tab === 'members' && (
              <div className="space-y-0.5">
                {portfolio.members.map(m => <MemberRow key={m.id} m={m} />)}
              </div>
            )}

            {/* Drift tab — sector-cascade rebalance monitoring (Phase A snapshot, read-only) */}
            {tab === 'drift' && <RebalanceDriftPanel portfolioId={portfolio.id} />}

            {/* Performance tab — R3 */}
            {tab === 'performance' && (
              perfLoading ? (
                <div className="py-6 text-center text-gray-400 text-sm">Loading performance…</div>
              ) : perf ? (
                <div className="space-y-4">
                  {/* Portfolio KPI tiles */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'Allocated',       value: fmt$(perf.total_allocated),   cls: 'text-gray-800 dark:text-gray-100' },
                      { label: 'Unrealised P&L',  value: fmtPnl(perf.total_unrealized), cls: pnlCls(perf.total_unrealized) },
                      { label: 'Total Value',      value: fmt$(perf.total_value),       cls: 'text-gray-800 dark:text-gray-100' },
                      { label: 'Return',           value: fmtPct(perf.total_return_pct, 2), cls: pnlCls(perf.total_return_pct) },
                    ].map(tile => (
                      <div key={tile.label} className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-3 border border-gray-200 dark:border-gray-800">
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{tile.label}</p>
                        <p className={`text-lg font-bold tabular-nums ${tile.cls}`}>{tile.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Member breakdown */}
                  <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase tracking-wider text-gray-400 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
                        <tr>
                          <th className="text-left px-3 py-2">Strategy</th>
                          <th className="text-right px-3 py-2">Wt%</th>
                          <th className="text-right px-3 py-2">Alloc</th>
                          <th className="text-right px-3 py-2">Unrealised</th>
                          <th className="text-right px-3 py-2">Return</th>
                          <th className="text-right px-3 py-2">Signal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
                        {perf.members.map(m => (
                          <tr key={m.strategy_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full ${m.is_active ? 'bg-green-400' : 'bg-gray-400'}`} />
                                <span className="text-gray-700 dark:text-gray-300 truncate max-w-[160px]">{m.strategy_name.split('·').pop()?.trim() || m.strategy_name}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-brand-gold font-medium">{(m.weight * 100).toFixed(0)}%</td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">{fmt$(m.allocation_usd)}</td>
                            <td className={`px-3 py-2 text-right tabular-nums font-medium ${pnlCls(m.unrealized_pnl)}`}>{fmtPnl(m.unrealized_pnl)}</td>
                            <td className={`px-3 py-2 text-right tabular-nums ${pnlCls(m.return_pct)}`}>{fmtPct(m.return_pct, 2)}</td>
                            <td className="px-3 py-2 text-right">
                              {m.today_signal ? (
                                <span className={`text-[10px] font-medium ${
                                  m.today_signal.type === 'BUY'  ? 'text-green-400' :
                                  m.today_signal.type === 'SELL' ? 'text-red-400'   : 'text-gray-400'
                                }`}>{m.today_signal.type}</span>
                              ) : <span className="text-gray-300">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="py-6 text-center text-gray-400 text-sm">No performance data</div>
              )
            )}

            {/* Net Signals tab — R4 */}
            {tab === 'netsignals' && (
              netLoading ? (
                <div className="py-6 text-center text-gray-400 text-sm">Loading signals…</div>
              ) : netSigs ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">{netSigs.signal_date} · {netSigs.total_symbols} symbols</p>
                    {netSigs.cancelled_pairs > 0 && (
                      <span className="text-[10px] text-brand-gold font-medium">
                        {netSigs.cancelled_pairs} opposing pair{netSigs.cancelled_pairs > 1 ? 's' : ''} cancelled
                      </span>
                    )}
                  </div>

                  {netSigs.netted_orders.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No signals generated today</p>
                  ) : (
                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800/60 px-3">
                      {netSigs.netted_orders.map(item => (
                        <NetSignalRow key={item.symbol} item={item} />
                      ))}
                    </div>
                  )}

                  {netSigs.netted_orders.length > 0 && (
                    <p className="text-[10px] text-gray-400">{netSigs.note}</p>
                  )}
                </div>
              ) : (
                <div className="py-6 text-center text-gray-400 text-sm">No signal data</div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const BasketsPage = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading,    setLoading]    = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tradingService.listPortfolios();
      setPortfolios(res.data?.results || res.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    const res = await tradingService.deletePortfolio(id, { cascadeStrategies: true });
    setPortfolios(prev => prev.filter(p => p.id !== id));
    const d = (res && res.data) || {};
    const nDel = (d.strategies_deleted || []).length;
    const kept = d.strategies_kept || [];
    let msg = `Basket deleted — ${nDel} strateg${nDel === 1 ? 'y' : 'ies'} removed.`;
    if (kept.length) {
      msg += `\n\n${kept.length} kept (used elsewhere / active / holding):\n`
        + kept.slice(0, 12).map(k => `• ${k.name}: ${(k.reasons || []).join(', ')}`).join('\n');
      if (kept.length > 12) msg += `\n…and ${kept.length - 12} more`;
    }
    window.alert(msg);
  };

  const handleToggleActive = async (id) => {
    // Optimistic flip of the container badge for snappiness…
    setPortfolios(prev => prev.map(p =>
      p.id === id ? { ...p, is_active: !p.is_active } : p
    ));
    const res = await tradingService.togglePortfolioActive(id);
    if (res?.data?.signals_held_until) window.alert('Orders for this basket will be staged at 09:31 ET, after the live account\u2019s pre-open release, and go out at the next open.');
    // …then a full reload so cascaded member is_active states (and any
    // liquidation that just staged) are reflected, not just the container flag.
    await load();
  };

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading baskets…</div>;

  if (portfolios.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-3xl mb-3">📊</p>
        <p className="text-gray-500 text-sm mb-1">No baskets deployed yet.</p>
        <p className="text-gray-400 text-xs">Pin strategies in the Analytics → Overview table and click <strong>Portfolio Builder</strong> to deploy a basket.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-serif text-lg text-gray-900 dark:text-gray-100">Strategy Baskets</h2>
        <button onClick={load} className="text-xs text-gray-400 hover:text-brand-gold transition-colors">↺ Refresh</button>
      </div>

      {portfolios.map(p => (
        <BasketCard
          key={p.id}
          portfolio={p}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
        />
      ))}
    </div>
  );
};

export default BasketsPage;
